import { useState, useEffect, useCallback, useContext } from "react";
import { UserContext } from '../Context/UserContext';
import "../styles/pitchMatchupReport.css";
import HomeGameBar from "./HomeGameBar";
import NotPremium from "./NotPremium";

// ─── Helpers ────────────────────────────────────────────────────────────────

const STARS = (n) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={`star ${i < n ? "filled" : "empty"}`}>★</span>
  ));

const TIER_LABEL = {
  elite: { label: "Elite",     cls: "tier-elite" },
  good:  { label: "Strong",    cls: "tier-good"  },
  avg:   { label: "Favorable", cls: "tier-avg"   },
  below: { label: "Poor",      cls: "tier-below" },
};

const fmt = (val, decimals = 3) =>
  val != null ? Number(val).toFixed(decimals) : "—";

const fmtPct = (val) =>
  val != null ? `${Number(val).toFixed(1)}%` : "—";

// ─── PitchRow ────────────────────────────────────────────────────────────────

function PitchRow({ code, data }) {
  if (!data) return null;

  const { pitch_type_name, usage_pct, pitch_score, good_matchup, partial_matchup,
          stats_at_good_or_better, stats_at_avg_or_better, reliable, stats } = data;

  const goodBadge = stats_at_good_or_better != null
    ? `${stats_at_good_or_better}/5`
    : "—";

  const rowClass = good_matchup ? "good-matchup" : partial_matchup ? "partial-matchup" : "";

  return (
    <tr className={`pitch-row ${rowClass}`}>
      <td className="pitch-code">{code}</td>
      <td className="pitch-name">{pitch_type_name}</td>
      <td className="pitch-usage">{fmtPct(usage_pct)}</td>
      <td className="pitch-score">
        {pitch_score != null ? (
          <>
            {Number(pitch_score).toFixed(1)}
            {!reliable && <span className="low-pa-flag">*</span>}
          </>
        ) : "—"}
      </td>
      <td className="pitch-pa">{stats?.pa_count ?? "—"}</td>
      <td className="pitch-good">
        <span className={`good-badge ${good_matchup ? "badge-yes" : partial_matchup ? "badge-partial" : stats_at_good_or_better != null ? "badge-no" : "badge-na"}`}>
          {good_matchup
            ? `✓ ${goodBadge}`
            : partial_matchup
            ? `½ ${stats_at_avg_or_better}/5`
            : stats_at_good_or_better != null
            ? `✗ ${goodBadge}`
            : "—"}
        </span>
      </td>
    </tr>
  );
}

// ─── StatRow ─────────────────────────────────────────────────────────────────

function StatRow({ code, data }) {
  if (!data?.stats) return null;
  const { stats, pitch_type_name, tier_results } = data;
  const tr = tier_results || {};

  const cell = (metric, val, decimals = 3) => {
    const tier = tr[metric];
    const info = TIER_LABEL[tier];
    return (
      <td key={metric} className={`stat-val ${info?.cls || ""}`}>
        {fmt(val, decimals)}
        {info && <span className="tier-dot" title={info.label} />}
      </td>
    );
  };

  return (
    <tr className="stat-row">
      <td className="pitch-code">{code}</td>
      <td className="pitch-name">{pitch_type_name}</td>
      {cell("ba",                stats.ba)}
      {cell("xba",               stats.xba)}
      {cell("ops",               stats.ops)}
      {cell("hard_contact_rate", stats.hard_contact_rate, 1)}
      {cell("woba",              stats.woba)}
    </tr>
  );
}

// ─── BreakdownTable ───────────────────────────────────────────────────────────

function BreakdownTable({ breakdown, label }) {
  if (!breakdown || Object.keys(breakdown).length === 0) {
    return <p className="no-data">No data available for {label}.</p>;
  }

  const sorted = Object.entries(breakdown).sort(
    ([, a], [, b]) => (b.usage_pct || 0) - (a.usage_pct || 0)
  );

  return (
    <div className="breakdown-section">
      <h4 className="breakdown-label">{label}</h4>

      <div className="table-wrap">
        <table className="pitch-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Usage</th>
              <th>Score</th>
              <th>PA</th>
              <th>Good?</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(([code, data]) => (
              <PitchRow key={code} code={code} data={data} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-wrap mt-12">
        <table className="pitch-table stat-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>BA</th>
              <th>xBA</th>
              <th>OPS</th>
              <th>HardCt%</th>
              <th>wOBA</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(([code, data]) => (
              <StatRow key={code} code={code} data={data} />
            ))}
          </tbody>
        </table>
      </div>

      <p className="low-pa-note">* Low sample size — treat as directional only</p>
    </div>
  );
}

// ─── PitchMatchupCard ─────────────────────────────────────────────────────────

export function PitchMatchupCard({ report, defaultExpanded = false }) {
  const { state } = useContext(UserContext);
  const [expanded,  setExpanded]  = useState(defaultExpanded);
  const [detail,    setDetail]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState("overall");

  const threeYearStat = (state.dailyThreeYearStats || []).find(
    (s) => s.hitter_name === report.hitter.name
  );
  const matchupRating = threeYearStat?.hit_matchup_rating ?? null;

  const matchupRatingStyle = () => {
    if (matchupRating === null) return {};
    if (matchupRating >= 0.450) return { backgroundColor: "lightblue" };
    if (matchupRating >= 0.400) return { backgroundColor: "lightgreen" };
    if (matchupRating > 0.333)  return { backgroundColor: "lightyellow" };
    if (matchupRating <= 0.230) return { backgroundColor: "lightpink" };
    return { backgroundColor: "#d0d0d0" };
  };

  const fetchDetail = useCallback(async () => {
    if (detail) return;
    setLoading(true);
    try {
      const res = await fetch(`/pitch_matchup_reports/${report.id}`);
      const data = await res.json();
      setDetail(data);
    } catch (e) {
      console.error("Failed to load report detail", e);
    } finally {
      setLoading(false);
    }
  }, [report.id, detail]);

  useEffect(() => {
    if (defaultExpanded) {
      fetchDetail();
    }
  }, [defaultExpanded]);

  const toggle = () => {
    if (!expanded) fetchDetail();
    setExpanded((e) => !e);
  };

  const splitLabel = report.split === "vs_lhp" ? "vs LHP" : "vs RHP";
  const handLabel  = report.pitcher?.throw_hand === "L" ? "vs LHP" : "vs RHP";

  const showSplitData = activeTab === "split" && detail;

  const displayArsenalPct = showSplitData
    ? report.arsenal_pct
    : report.overall_arsenal_pct;

  const displayStars = showSplitData
    ? report.stars
    : (report.overall_stars ?? report.stars);

  const showLowData = showSplitData
    ? report.low_data
    : report.overall_low_data;

  const dataCoveragePct = showSplitData
    ? report.data_coverage_pct
    : report.overall_data_coverage_pct;

  return (
    <div className={`matchup-card ${expanded ? "expanded" : ""}`}>
      <div className="card-summary" onClick={toggle} role="button" tabIndex={0}
           onKeyDown={(e) => e.key === "Enter" && toggle()}>

        <div className="card-players">
          <span className="hitter-name">{report.hitter.name}</span>
          <span className="vs-divider">vs</span>
          <span className="pitcher-name">{report.pitcher.name}</span>
          <span className="team-tags">
            <span className="team-tag">{report.hitter.team}</span>
            <span className="team-tag">{report.pitcher.team}</span>
          </span>
        </div>

        <div className="card-scores">
          {matchupRating !== null ? (
            <div className="matchup-rating" style={matchupRatingStyle()}>
              <span className="matchup-rating-value">{matchupRating.toFixed(3)}</span>
              <span className="matchup-rating-label">Matchup Rtg</span>
            </div>
          ) : (
            <div className="matchup-rating no-data">
              <span className="matchup-rating-value">—</span>
              <span className="matchup-rating-label">Not enough data</span>
            </div>
          )}

          <div className="arsenal-pct">
            <span className="pct-value">{fmtPct(displayArsenalPct)}</span>
            <span className="pct-label">
              Arsenal Coverage
              {showLowData && (
                <span
                  className="low-data-flag"
                  title={`Only ${dataCoveragePct?.toFixed(0)}% of arsenal has enough data`}
                >
                  ⚠ Low Data
                </span>
              )}
            </span>
          </div>

          <div className="star-rating">
            {STARS(displayStars)}
            {showLowData && (
              <span
                className="low-data-star-flag"
                title={`Low data — only ${dataCoveragePct?.toFixed(0)}% of arsenal has enough PA`}
              >
                ⚠
              </span>
            )}
          </div>

          <div className="split-badge">{splitLabel}</div>
        </div>

        <button className="expand-btn" aria-label={expanded ? "Collapse" : "Expand"}>
          <span className={`chevron ${expanded ? "up" : "down"}`}>›</span>
        </button>
      </div>

      {expanded && (
        <div className="card-detail">
          {loading && <div className="detail-loading">Loading breakdown...</div>}

          {detail && (
            <>
              <div className="tab-bar">
                <button
                  className={`tab-btn ${activeTab === "split" ? "active" : ""}`}
                  onClick={() => setActiveTab("split")}
                >
                  {handLabel}
                </button>
                <button
                  className={`tab-btn ${activeTab === "overall" ? "active" : ""}`}
                  onClick={() => setActiveTab("overall")}
                >
                  Overall
                </button>
              </div>

              {activeTab === "split" && (
                <BreakdownTable breakdown={detail.breakdown} label={handLabel} />
              )}
              {activeTab === "overall" && (
                <BreakdownTable breakdown={detail.overall_breakdown} label="Overall" />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PitchMatchupReportPage ───────────────────────────────────────────────────

export function PitchMatchupReportPage({ hitterId = null, hitterName = null, date = null }) {
  const { state, isActiveSub } = useContext(UserContext);
  const [minStars,     setMinStars]     = useState(1);
  const [searchQuery,  setSearchQuery]  = useState("");

  const allReports = state.dailyPitchReports || [];

  const reports = allReports
    .filter((r) => {
      if (minStars > 1 && r.overall_stars < minStars) return false;
      if (hitterId && r.hitter.id !== hitterId) return false;
      if (hitterName && r.hitter.name.toLowerCase() !== hitterName.toLowerCase()) return false;
      if (date && r.game_date !== date) return false;
      if (searchQuery && !r.hitter.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => (b.overall_arsenal_pct || 0) - (a.overall_arsenal_pct || 0));

  return (
    <div>
      {isActiveSub ? (
        <div>
          <HomeGameBar />
          <div className="pitch-report-page">
            <div className="page-header">
              <div className="header-left">
                <h1 className="page-title">Pitch Matchup Reports</h1>
                <p className="page-subtitle">
                  {date || new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <div className="header-filters">
                <label className="filter-label">Min Stars</label>
                <div className="star-filter">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      className={`star-filter-btn ${minStars === n ? "active" : ""}`}
                      onClick={() => setMinStars(n)}
                    >
                      {"★".repeat(n)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="header-search">
              <input
                type="text"
                className="search-input"
                placeholder="Search hitter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {reports.length === 0 && (
              <div className="page-empty">
                No matchup reports found{minStars > 1 ? ` at ${minStars}+ stars` : " for today"}.
              </div>
            )}

            {reports.length > 0 && (
              <div className="report-count">
                {reports.length} matchup{reports.length !== 1 ? "s" : ""}
                {minStars > 1 ? ` at ${minStars}+ stars` : ""}
              </div>
            )}

            <div className="matchup-list">
              {reports.map((report) => (
                <PitchMatchupCard key={report.id} report={report} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <NotPremium />
      )}
    </div>
  );
}

export default PitchMatchupReportPage;